import React, { useEffect, useRef, useState } from 'react';
import { Button, Collapse } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import NeedHelp from 'components/NeedHelp';
import { CALENDLY_LINK } from 'constants/common';
import { PATH } from 'constants/path';
import { getStartedInsuranceBilling } from 'api/insuranceBilling';
import useCurrentUser from 'hooks/useCurrentUser';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import type { TInsuranceBillingStatus } from 'types/insuranceBilling';
import { isBillingOwnerOrAdmin } from './access';
import s from './s.module.less';
import heroIllustration from './imgs/hero-illustration.png';
import benefitCoins from './imgs/benefit-coins.png';
import benefitDocument from './imgs/benefit-document.png';
import step1Icon from './imgs/step-1-document.svg';
import step2Icon from './imgs/step-2-send.svg';
import step3Icon from './imgs/step-3-payment.svg';

const { Panel } = Collapse;

type TabKey = 'benefits' | 'how-it-works' | 'pricing' | 'faqs';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'benefits', label: 'Benefits' },
    { key: 'how-it-works', label: 'How it works' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'faqs', label: 'FAQs' },
];

const STEPS = [
    {
        num: 'Step 1',
        title: 'Set up your billing profile',
        body: 'Add your billing info, connect your EHR, and set up a payment method. The one-time setup takes less than 8 minutes, then Klarity can submit claims for you.',
        icon: step1Icon,
    },
    {
        num: 'Step 2',
        title: 'Klarity files the claim within 24 hours',
        body: 'After each session, Klarity drafts the claim with the right codes and submits it directly to the payer. If a claim gets denied, Klarity diagnoses the cause and files the appeal for you.',
        icon: step2Icon,
    },
    {
        num: 'Step 3',
        title: 'You get paid, pay only 3.9%',
        body: "Reimbursements land in your account, minus a 3.9% fee per successful claim. If a claim doesn't get paid, you pay nothing.",
        icon: step3Icon,
    },
];

// FAQ 按原型修订：AskChart 全部改为 Klarity；旧的「How much does AskChart cost?」订阅档位/credits 条目
// 已移除（那是 AskChart 旧套餐定价，insurance billing 只有 3.9% managed-billing 模式）
const FAQS: { key: string; question: string; answer: React.ReactNode }[] = [
    {
        key: '1',
        question: 'What does "Managed Billing" include?',
        answer: 'Managed Billing is an optional service at 3.9% of successful reimbursements (you only pay when you get paid). It includes same-day claim submission, denial management & auto-appeals, insurance eligibility verification, and on-demand & weekly billing reports. There are no separate fees: everything is bundled into the 3.9% rate.',
    },
    {
        // Medicare/Medicaid 合规限制不能按比例抽成，按 claim 提交收固定费；与 pricing 区脚注呼应
        key: 'medicare-medicaid',
        question: 'How are Medicare and Medicaid claims billed?',
        answer: (
            <>
                <p>
                    Klarity charges a flat fee per claim submitted, regardless of whether the claim is paid, denied, or
                    rejected.
                </p>
                <ul>
                    <li>
                        Psychiatry and behavioral health, Medicare and Medicaid:{' '}
                        <strong>$3.50 per claim submitted.</strong>
                    </li>
                    <li>
                        All other specialties: fee varies by specialty. For specialties with average reimbursement under
                        $500 per claim, the fee is <strong>$4.00 per claim submitted.</strong>
                    </li>
                </ul>
            </>
        ),
    },
    {
        key: '2',
        question: 'How is Klarity different from a virtual assistant or billing agency?',
        answer: 'Klarity operates 24/7 inside the EHR with no turnover, no sick days, and no ramp-up time. Managed Billing charges 3.9% of successful reimbursements, roughly half what traditional billing agencies charge (5 to 10%). A typical solo practice saves $4K+ per month compared to hiring a VA plus a billing agency, with a 94% clean claim rate versus the 70 to 80% industry average.',
    },
    {
        key: '5',
        question: 'Is Klarity HIPAA compliant?',
        answer: 'Yes. Klarity is fully HIPAA compliant with a standard Business Associate Agreement (BAA) in place. All patient data is encrypted end-to-end using AES-256 (in transit and at rest). No PHI is ever used for AI model training. Klarity maintains full audit logs and provides 24-hour preliminary breach notification with a 60-day full report.',
    },
    {
        key: '6',
        question:
            'How is provider data handled within your AI-assisted processes, and what safeguards are in place for sensitive information?',
        answer: 'Klarity is fully HIPAA compliant with a standard Business Associate Agreement (BAA) in place. All patient data is encrypted and handled according to federal privacy standards.',
    },
];

type Props = {
    // gate（index.tsx）已拉取的 /status VO，避免二次请求
    status?: TInsuranceBillingStatus | null;
    // dashboard 底部「How it works」入口：只读浏览 landing —— 顶部显示 Back 返回 dashboard，
    // 隐藏 Get started / Webinars / Learn more 等 CTA（用户已开通，无需再引导）
    viewOnly?: boolean;
    onBack?: () => void;
};

const InsuranceBillingLanding = ({ status, viewOnly, onBack }: Props) => {
    const navigate = useNavigate();
    const wrapRef = useRef<HTMLDivElement>(null);

    // viewOnly 由 dashboard 整页切换而来（非路由跳转），滚动容器会保留 dashboard 的滚动位置，进来先回到顶部
    useEffect(() => {
        if (viewOnly) {
            wrapRef.current?.scrollIntoView({ block: 'start' });
        }
    }, [viewOnly]);
    // 仅用 refetch：get-started 成功后刷新全局缓存，避免从向导返回时 gate 还拿旧的未绑定状态
    const [, , refetchStatus] = useInsuranceBillingStatus();
    const [currentUser] = useCurrentUser();
    const [activeTab, setActiveTab] = useState<TabKey>('benefits');
    // 与 Credentialing landing 一致：Sign-up for Webinars 打开 Webinars & Resources 抽屉（内嵌 Calendly）
    const [showWebinars, setShowWebinars] = useState(false);
    const [gettingStarted, setGettingStarted] = useState(false);

    // 团队 provider：隐藏 Get started 等 CTA，改为提示 banner 让其找 owner 开通。
    // 视角判定走 currentUser 而非 canBind（OPS 代登录下 canBind 恒 false，ops 该看到 owner 本来的
    // landing 而非「去找你的 owner」banner，见 access.ts）；能否真正发起绑定仍以 canBind 为准（下方 canStart）
    const isTeamProvider = !!currentUser && !isBillingOwnerOrAdmin(currentUser);
    // 发起绑定的资格仍由 /status 的 canBind 权威判定：ops 代登录只可看不可代 provider 发起 → 主 CTA 禁用
    const canStart = !!status?.canBind;
    // bound 但 onboarding 未完成 → 主 CTA 文案 Resume（onboardingCompleted 为 null 表示未知，同样放行续填）
    const onboardingStarted = !!status?.bound && status?.onboardingCompleted !== true;
    // 已 bound 但 onboardingStatus 为 null = 上游取不到（SA key 解密失败 / AskChart 不可用，接口报 91025 等）。
    // 刚 get-started 的新用户 /status 即为 in_progress，不会误命中 → 提示不可用并禁用 Resume
    const billingUnavailable = !!status?.bound && status?.onboardingStatus == null;

    // 首次点击 Get started：先调 get-started 绑定（幂等，失败后再点即修复），成功后进 onboarding 向导；
    // 已 bound（Resume）则直接跳转
    const handleGetStarted = async () => {
        if (gettingStarted) return;
        if (status?.bound) {
            navigate(PATH.INSURANCE_BILLING_ONBOARDING);
            return;
        }
        setGettingStarted(true);
        try {
            const res: any = await getStartedInsuranceBilling();
            if (!res?.error) {
                refetchStatus();
                navigate(PATH.INSURANCE_BILLING_ONBOARDING);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setGettingStarted(false);
        }
    };

    const handleTabClick = (key: TabKey) => {
        setActiveTab(key);
        const el = document.getElementById(`section-${key}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className={s.wrap} ref={wrapRef}>
            {viewOnly && (
                <Button type="link" className={s.backBtn} icon={<ArrowLeftOutlined />} onClick={onBack}>
                    Back
                </Button>
            )}
            {/* Hero */}
            <div className={s.hero}>
                <div className={s.heroLeft}>
                    <h1 className={s.heroTitle}>
                        Get paid faster with <span className={s.heroTitleAccent}>Managed billing</span>
                        <span className={s.heroBetaTag}>BETA</span>
                    </h1>
                    <p className={s.heroSub}>
                        Automatically submit claims, fight denials, and track reimbursements after every session. No
                        biller needed. Works 24/7.
                    </p>
                    {!viewOnly && isTeamProvider && (
                        // 团队 provider：billing 是 practice 级服务，只能由 owner 开通 —— 隐藏 CTA，提示找 owner
                        <div className={s.teamBanner}>
                            <span className={s.teamBannerIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                                        fill="#3B82F6"
                                    />
                                    <path
                                        d="M10 9V14M10 6.5V6.51"
                                        stroke="#fff"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            <span>
                                Managed billing covers your whole practice, so it only needs to be set up once. Just let
                                your practice owner know you would like it turned on, and your claims will start showing
                                up here.
                            </span>
                        </div>
                    )}
                    {!viewOnly && !isTeamProvider && billingUnavailable && (
                        <div className={s.unavailableBanner}>
                            <span className={s.teamBannerIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                                        fill="#F59E0B"
                                    />
                                    <path
                                        d="M10 6V11M10 13.5V13.51"
                                        stroke="#fff"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            <span>
                                Managed billing is temporarily unavailable for your practice. Our team has been
                                notified. If this persists, please contact support.
                            </span>
                        </div>
                    )}
                    {!viewOnly && !isTeamProvider && (
                        <div className={s.heroCtas}>
                            {/* 原型改版：外链注册改为进入 in-app onboarding；首次点击先调 get-started 绑定，
                                bound 未完成时文案 Resume 续填 */}
                            <Button
                                type="primary"
                                className={s.ctaPrimary}
                                disabled={gettingStarted || !canStart || billingUnavailable}
                                onClick={handleGetStarted}
                            >
                                {onboardingStarted ? 'Resume' : 'Get started'}
                            </Button>
                            {/* 原型改版：Book a demo（外链 Typeform）移除，换成 Sign-up for Webinars */}
                            <Button className={s.ctaSecondary} onClick={() => setShowWebinars(true)}>
                                Sign-up for Webinars
                            </Button>
                            <Button type="text" className={s.ctaTertiary} onClick={() => handleTabClick('benefits')}>
                                Learn more <span className={s.arrow}>→</span>
                            </Button>
                        </div>
                    )}
                </div>
                <div className={s.heroRight}>
                    <img src={heroIllustration} alt="" className={s.heroImage} />
                </div>
            </div>

            {/* Tab nav (sticky) */}
            <div className={s.tabNavSticky}>
                <div className={s.tabNav}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`${s.tabItem} ${activeTab === tab.key ? s.tabItemActive : ''}`}
                            onClick={() => handleTabClick(tab.key)}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Benefits */}
            <section id="section-benefits" className={s.section}>
                <h2 className={s.sectionTitle}>Explore the benefits of Managed billing</h2>
                <div className={s.benefitsGrid}>
                    <div className={`${s.benefitCard} ${s.benefitCardGreen} ${s.benefitCardTall}`}>
                        <div className={s.benefitStatRow}>
                            <span className={s.benefitStat}>3.9%</span>
                            <span className={s.benefitStatLabel}>industry-best rate</span>
                        </div>
                        <div className={s.benefitSub}>Pay only when paid</div>
                        <img src={benefitCoins} alt="" className={`${s.benefitIllustration} ${s.illustrationCoins}`} />
                    </div>

                    <div className={`${s.benefitCard} ${s.benefitCardBlue}`}>
                        <div className={s.benefitStatRow}>
                            <span className={s.benefitStatMd}>94%</span>
                            <span className={s.benefitStatLabelMd}>first pass rate</span>
                        </div>
                        <div className={s.benefitSub}>vs. 70 to 80% industry avg</div>
                    </div>

                    <div className={`${s.benefitCard} ${s.benefitCardYellow}`}>
                        <div className={s.benefitStatRow}>
                            <span className={s.benefitStatMd}>&lt;24 hr</span>
                        </div>
                        <div className={`${s.benefitStatLabelMd} ${s.benefitBlockLabel}`}>claim filed same-day</div>
                        <div className={s.benefitSub}>after every session</div>
                    </div>

                    <div className={`${s.benefitCard} ${s.benefitCardGray} ${s.benefitCardWide}`}>
                        <div className={s.benefitWideText}>
                            <div className={s.benefitStat}>0 denials left unworked</div>
                            <div className={s.benefitSub}>every denial appealed</div>
                        </div>
                        <img src={benefitDocument} alt="" className={`${s.benefitIllustration} ${s.illustrationDoc}`} />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="section-how-it-works" className={s.section}>
                <h2 className={s.sectionTitle}>How Managed billing works</h2>
                <div className={s.stepsGrid}>
                    {STEPS.map((step) => (
                        <div key={step.num} className={s.stepCard}>
                            <div className={s.stepIcon}>
                                <img src={step.icon} alt="" className={s.stepIconImg} />
                            </div>
                            <div className={s.stepNum}>{step.num}</div>
                            <div className={s.stepTitle}>{step.title}</div>
                            <div className={s.stepBody}>{step.body}</div>
                        </div>
                    ))}
                </div>
                {/* 原型改版：与 Credentialing landing 一致的支持邮箱行 */}
                <p className={s.howitHelp}>
                    Questions about insurance billing? Email us at{' '}
                    <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a>
                </p>
            </section>

            {/* Pricing */}
            <section id="section-pricing" className={s.section}>
                <h2 className={s.sectionTitle}>Transparent pricing</h2>
                <div className={s.pricingGrid}>
                    <div className={`${s.pricingCard} ${s.pricingCardActive}`}>
                        <div className={s.pricingLabel}>KLARITY</div>
                        <div className={s.pricingValue}>
                            3.9%<sup className={s.pricingAsterisk}>*</sup>
                        </div>
                        <div className={s.pricingBody}>
                            of successful reimbursements. You only pay when we get you paid.
                        </div>
                    </div>
                    <div className={s.pricingCard}>
                        <div className={s.pricingLabel}>LEGACY 3RD-PARTY BILLERS</div>
                        <div className={`${s.pricingValue} ${s.pricingValueStrike}`}>5 to 10%</div>
                        <div className={s.pricingBody}>plus setup &amp; monthly fees.</div>
                    </div>
                </div>
                {/* Medicare/Medicaid 合规不能按比例抽成，改按 claim 收费；详情见 FAQ「How are Medicare and Medicaid claims billed?」 */}
                <p className={s.pricingFootnote}>
                    * For Medicare and Medicaid claims, you&apos;ll be charged on a claim submission basis to stay
                    compliant. See the FAQ below for the per claim rates.
                </p>
            </section>

            {/* FAQs */}
            <section id="section-faqs" className={s.section}>
                <h2 className={s.sectionTitle}>Frequently asked questions</h2>
                <Collapse className={s.faqCollapse} bordered={false} expandIconPosition="end" ghost>
                    {FAQS.map((faq) => (
                        <Panel key={faq.key} header={<span className={s.faqQuestion}>{faq.question}</span>}>
                            <div className={s.faqAnswer}>{faq.answer}</div>
                        </Panel>
                    ))}
                </Collapse>
            </section>

            <NeedHelp
                show={showWebinars}
                onClose={() => setShowWebinars(false)}
                webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR}
            />
        </div>
    );
};

export default InsuranceBillingLanding;
